import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { ShareButton } from "@/components/ShareButton";
import { RatesBar } from "@/components/RatesBar";
import { FolderSelect } from "@/components/FolderSelect";
import { deriveFreeTitle } from "@/lib/titleUtils";
import { outlineToMindmap } from "@/lib/mindmapConvert";
import { AiImproveModal } from "@/components/AiImproveModal";
import { CalcTableModal } from "@/components/CalcTableModal";
import { SheetLibraryModal } from "@/components/SheetLibraryModal";
import { NotacoraSheet } from "@/extensions/NotacoraSheet";
import { toEditorHtml, htmlSheetsToTables, htmlToMarkdown } from "@/lib/noteEditorHtml";
import { newSheetId } from "@/lib/sheetEngine";
import { PAGE, BTN_PRIMARY, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, Save, Check, FileDown, FilePlus, Network, Sparkles, Table2, Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, List, ListOrdered, Quote, Link as LinkIcon, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

function FmtBtn({ onClick, active, title, testid, disabled, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${disabled ? "text-slate-300 dark:text-slate-700 cursor-not-allowed" : active ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
    >
      {children}
    </button>
  );
}

export default function NoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const titleRef = useRef(null);
  const didInit = useRef(false);
  const pending = useRef(null);
  const [, force] = useState(0);

  const saveAsTemplate = useCallback(async (cells) => {
    const name = window.prompt("Nombre de la plantilla:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/sheet-templates", { name: name.trim(), cells });
      toast.success("Plantilla guardada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, link: { openOnClick: false, autolink: true } }),
      Placeholder.configure({ placeholder: "Escribe aquí… usa la barra para dar formato o inserta una hoja." }),
      NotacoraSheet.configure({ onSaveTemplate: saveAsTemplate }),
    ],
    editorProps: {
      attributes: {
        class: "tiptap prose prose-slate dark:prose-invert max-w-none min-h-[50vh] focus:outline-none [&_h1]:font-heading [&_h2]:font-heading [&_a]:text-indigo-500",
        "data-testid": "note-content-input",
      },
    },
    onUpdate: () => { queueMicrotask(() => { setDirty(true); force((x) => x + 1); }); },
  });

  useEffect(() => {
    let ignored = false;
    api.get(`/notes/${id}`).then(({ data }) => {
      if (ignored) return;
      setTitle(data.title);
      setFolderId(data.folder_id || "");
      if (editor && !didInit.current) {
        editor.commands.setContent(toEditorHtml(data.content || ""), false);
        didInit.current = true;
      } else {
        // stash until editor ready
        pending.current = data.content || "";
      }
      if (["Nueva nota", "Sin título"].includes((data.title || "").trim())) {
        setTimeout(() => { titleRef.current?.focus(); titleRef.current?.select(); }, 120);
      }
    }).catch((err) => {
      if (ignored) return;
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/");
    });
    return () => { ignored = true; };
  }, [id, navigate, editor]);

  useEffect(() => {
    if (editor && !didInit.current && pending.current != null) {
      editor.commands.setContent(toEditorHtml(pending.current), false);
      didInit.current = true;
      pending.current = null;
    }
  }, [editor]);

  const save = useCallback(async () => {
    if (!editor) return;
    setSaving(true);
    const html = editor.getHTML();
    let finalTitle = title;
    const t = (title || "").trim();
    if (!t || t === "Nueva nota" || t === "Sin título") {
      const d = deriveFreeTitle(editor.getText());
      if (d) { finalTitle = d; setTitle(d); }
    }
    try {
      await api.put(`/notes/${id}`, { title: finalTitle, content: html });
      setDirty(false);
      toast.success("Guardado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id, title, editor]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(save, 1200);
    return () => clearTimeout(t);
  }, [dirty, save]);

  const changeFolder = async (v) => {
    setFolderId(v);
    try {
      await api.put(`/notes/${id}`, { folder_id: v });
      toast.success(v ? "Movida a la carpeta" : "Quitada de la carpeta");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportPdf = () => {
    const bodyHtml = htmlSheetsToTables(editor?.getHTML() || "");
    const safeTitle = (title || "Nota").replace(/</g, "&lt;");
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
      <style>
        @page { margin: 20mm; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color:#0f172a; line-height:1.6; }
        h1.doc-title { font-size:26px; margin:0 0 6px; }
        .doc-meta { color:#64748b; font-size:12px; margin:0 0 24px; border-bottom:1px solid #e2e8f0; padding-bottom:12px; }
        h1,h2,h3 { line-height:1.25; } h1{font-size:22px} h2{font-size:18px} h3{font-size:16px}
        ul,ol { padding-left:22px; } code { background:#f1f5f9; padding:2px 4px; border-radius:4px; font-size:90%; }
        pre { background:#f1f5f9; padding:12px; border-radius:8px; overflow:auto; }
        a { color:#4f46e5; } blockquote { border-left:3px solid #cbd5e1; margin:0; padding-left:12px; color:#475569; }
        table { border-collapse:collapse; } td,th { border:1px solid #e2e8f0; padding:6px 10px; }
      </style></head><body>
      <h1 class="doc-title">${safeTitle}</h1>
      <p class="doc-meta">Notacora · ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</p>
      ${bodyHtml}
      </body></html>`);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1500);
    }, 400);
    toast.info('En el diálogo, elige "Guardar como PDF" para descargar.');
  };

  const saveToDocuments = async () => {
    try {
      const md = htmlToMarkdown(editor?.getHTML() || "");
      await api.post("/docs/create-pdf", { title: title || "Nota", content: md, folder_id: folderId || "", source: "note" });
      toast.success("Guardado como PDF en Documentos");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const convertToMap = async () => {
    const mtitle = title || "Nota";
    const outline = htmlToMarkdown(editor?.getHTML() || "");
    const mindmap = outlineToMindmap(mtitle, outline);
    try {
      const { data } = await api.post("/notes", { type: "mindmap", title: `${mtitle} (mapa)`, mindmap, folder_id: folderId || null });
      toast.success("Mapa mental creado desde tu nota");
      navigate(`/mindmap/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const isA = (name, attrs) => editor?.isActive(name, attrs);

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-2.5">
            <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="back-button"><ArrowLeft size={18} /></button>
            <FolderSelect value={folderId} onChange={changeFolder} />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono2 text-[11px] text-slate-400 flex items-center gap-1">
              {dirty ? "Editando..." : <><Check size={12} /> Guardado</>}
            </span>
            <button onClick={convertToMap} className={`${ICON_BTN} text-xs`} data-testid="convert-to-map" title="Convertir a mapa mental"><Network size={16} /></button>
            <button onClick={() => setLibOpen(true)} className={`${ICON_BTN} text-xs`} data-testid="insert-calc-table" title="Insertar hoja de cálculo"><Table2 size={16} /></button>
            <button onClick={() => setImproveOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white transition-all" data-testid="improve-ai-button" title="Mejorar con IA"><Sparkles size={15} /> IA</button>
            <button onClick={exportPdf} className={ICON_BTN} data-testid="export-pdf" title="Exportar a PDF"><FileDown size={18} /></button>
            <button onClick={saveToDocuments} className={ICON_BTN} data-testid="save-pdf-documents" title="Guardar como PDF en Documentos"><FilePlus size={18} /></button>
            <RatesBar />
            <ShareButton targetType="note" targetId={id} />
            <button onClick={save} className={BTN_PRIMARY} disabled={saving} data-testid="save-note"><Save size={15} /> {saving ? "..." : "Guardar"}</button>
          </div>
        </div>

        <input
          ref={titleRef}
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          className="w-full bg-transparent font-heading font-light text-3xl sm:text-4xl tracking-tight pb-4 mb-6 border-b border-slate-200 dark:border-slate-800 focus:outline-none focus:border-indigo-500 transition-colors"
          placeholder="Título de la nota"
          data-testid="note-title-input"
        />

        {/* Barra de formato visual */}
        <div className="flex flex-wrap items-center gap-1 mb-3 pb-3 border-b border-slate-200 dark:border-slate-800 sticky top-16 bg-white/80 dark:bg-slate-950/80 backdrop-blur z-10" data-testid="format-toolbar">
          <FmtBtn onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo()} title="Deshacer (Ctrl+Z)" testid="fmt-undo"><Undo2 size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo()} title="Rehacer (Ctrl+Y)" testid="fmt-redo"><Redo2 size={16} /></FmtBtn>
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
          <FmtBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={isA("bold")} title="Negrita (Ctrl+B)" testid="fmt-bold"><Bold size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={isA("italic")} title="Cursiva (Ctrl+I)" testid="fmt-italic"><Italic size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={isA("underline")} title="Subrayado (Ctrl+U)" testid="fmt-underline"><UnderlineIcon size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleStrike().run()} active={isA("strike")} title="Tachado" testid="fmt-strike"><Strikethrough size={16} /></FmtBtn>
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
          <FmtBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} active={isA("heading", { level: 1 })} title="Título 1" testid="fmt-h1"><Heading1 size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={isA("heading", { level: 2 })} title="Título 2" testid="fmt-h2"><Heading2 size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={isA("bulletList")} title="Lista" testid="fmt-list"><List size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={isA("orderedList")} title="Lista numerada" testid="fmt-ordered"><ListOrdered size={16} /></FmtBtn>
          <FmtBtn onClick={() => editor?.chain().focus().toggleBlockquote().run()} active={isA("blockquote")} title="Cita" testid="fmt-quote"><Quote size={16} /></FmtBtn>
          <FmtBtn onClick={() => {
            const prev = editor?.getAttributes("link")?.href || "";
            const url = window.prompt("Enlace (URL):", prev);
            if (url === null) return;
            if (url === "") { editor?.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
            editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          }} active={isA("link")} title="Enlace" testid="fmt-link"><LinkIcon size={16} /></FmtBtn>
        </div>

        <EditorContent editor={editor} />
      </div>

      <AiImproveModal
        open={improveOpen}
        mode="note"
        getText={() => editor?.getText() || ""}
        onApply={({ outline }) => { editor?.commands.setContent(toEditorHtml(outline || "")); setDirty(true); }}
        onClose={() => setImproveOpen(false)}
      />
      <CalcTableModal
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        onInsert={(cells) => { editor?.chain().focus().insertSheet({ sid: newSheetId(), cells }).run(); setDirty(true); }}
      />
      <SheetLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        onCreateNew={() => setCalcOpen(true)}
        onInsertCopy={(cells) => { editor?.chain().focus().insertSheet({ sid: newSheetId(), cells }).run(); setDirty(true); }}
        onInsertLinked={({ refId }) => { editor?.chain().focus().insertSheet({ sid: newSheetId(), refId, linked: true }).run(); setDirty(true); }}
      />
    </div>
  );
}
